// This module defines metadata that is **version-agnostic**: the metadata are
// associated with *concepts* whose semantics should not change across versions.
//
// Why and how does this work? The BAT concept is identified by BAT name; the
// BAT field concept by BAT name plus field name. It is a key constraint of BAT
// schema management that those names must point to a semantically consistent
// concept (that is, consistent across versions): from one version to the next,
// we must never change the underlying *conceptual meaning* (to a human) of the
// BAT schema with a particular name, or the schema field with a particular
// name. (When we have a new concept to express, such that a "description" would
// no longer be consistent across versions, we express it with a new BAT or
// field, not by adjusting an existing one.)
//
// TODO: **Why do we have `quantity` fields below for BATs that don't really
// have a `quantity` field?** (Like, logistics.) The problem we have is that, as
// of 2023-08-04, the BART-EM matching process during evaluation renames any
// BART columns used as the EM's functional unit to `quantity`. For example, the
// logistics EM uses `weight_quantity` as the functional unit; during
// evaluation, that gets renamed to `quantity`. Then downstream, when we're
// trying to populate TraceDetails with metadata, we only know that the column
// was named `quantity` -- we don't know that it was *originally*
// `weight_quantity`. Obviously, we need to fix this situation: it is not only
// confusing, but also only works because of assumptions that we do not want to
// make about EMs (e.g. that every EM that uses a BAT will always use the same
// column as the functional unit).

// Only a type import for assertions
// eslint-disable-next-line no-restricted-imports
import { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { BadDataError } from '@watershed/errors/BadDataError';
import { i18n } from '@watershed/intl';
import {
  assertNoTypeDifferences,
  noTypeDifferences,
} from '@watershed/shared-util/src/assertNoTypeDifferences';

import { captureException } from '../utils/sentry';
import { BatTypeNamesForTestingOnly, BusinessActivityTypeName } from './types';
import { buildingOccupationMetadata } from './batMetadata/buildingOccupation';
import { buildingStationaryCombustionMetadata } from './batMetadata/buildingStationaryCombustion';
import { buildingWasteMetadata } from './batMetadata/buildingWaste';
import { carbonCreditRetirementsMetadata } from './batMetadata/carbonCreditRetirements';
import { carbonRemovalMetadata } from './batMetadata/carbonRemoval';
import { cloudComputeUsageMetadata } from './batMetadata/cloudComputeUsage';
import { cloudSpendMetadata } from './batMetadata/cloudSpend';
import { corporate315InvestmentsMetadata } from './batMetadata/corporate315Investments';
import { dataTransferMetadata } from './batMetadata/dataTransfer';
import { directGhgEmissionsMetadata } from './batMetadata/directGhgEmissions';
import { electricityConsumptionMetadata } from './batMetadata/electricityConsumption';
import { electricityConsumptionDatacenterMetadata } from './batMetadata/electricityConsumptionDatacenter';
import { employeesMetadata } from './batMetadata/employees';
import { expenditureMetadata } from './batMetadata/expenditure';
import { expenditureMappedMetadata } from './batMetadata/expenditureMapped';
import { expenditureUnmappedMetadata } from './batMetadata/expenditureUnmapped';
import { financeCorporateAssetMetadata } from './batMetadata/financeCorporateAsset';
import { fuelCombustionMetadata } from './batMetadata/fuelCombustion';
import { lifetimeFuelAndElectricityUseOfSoldProductsMetadata } from './batMetadata/lifetimeFuelAndElectricityUseOfSoldProducts';
import { lodgingMetadata } from './batMetadata/lodging';
import { logisticsMetadata } from './batMetadata/logistics';
import { nonBuildingElectricityConsumptionMetadata } from './batMetadata/nonBuildingElectricityConsumption';
import { refrigerantsMetadata } from './batMetadata/refrigerants';
import { soldRefrigerantMetadata } from './batMetadata/soldRefrigerant';
import { soldRetailGoodMetadata } from './batMetadata/soldRetailGood';
import { soldVehicleMetadata } from './batMetadata/soldVehicle';
import { travelAirMetadata } from './batMetadata/travelAir';
import { travelPrivateJetsMetadata } from './batMetadata/travelPrivateJets';
import { travelRailMetadata } from './batMetadata/travelRail';
import { vehiclesMetadata } from './batMetadata/vehicles';
import { wasteMetadata } from './batMetadata/waste';
import { wasteProductsMetadata } from './batMetadata/wasteProducts';
import { waterUsageMetadata } from './batMetadata/waterUsage';
import type {
  FieldMetadataMessageDescriptorDef,
  BatMetadataConfigEntry,
} from './batMetadata/common/types';
export type { FieldMetadataMessageDescriptorDef, BatMetadataConfigEntry };

// This should never be displayed to users itself!
export const GLOBAL_KEY = '__GLOBAL__';

const cloudSpendCurrencyQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud spend',
    context: 'The spend on cloud services.',
  }),
  externalDescription: msg({
    message: 'The spend on cloud services.',
    context: 'Cloud spend',
  }),
};

const cloudUsageQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud usage quantity',
    context:
      'The number of instance hours for cloud compute, or bytes of networking or byte-seconds of storage resources.',
  }),
  externalDescription: msg({
    message:
      'The number of instance hours for cloud compute, or bytes of networking or byte-seconds of storage resources.',
    context: 'Cloud usage quantity',
  }),
};

const ghgRemovalQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'GHG removal',
    context: 'The quantity of GHGs removed.',
  }),
  externalDescription: msg({
    message: 'The quantity of GHGs removed.',
    context: 'GHG removal',
  }),
};
const ghgReversalsQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'GHG reversals',
    context: 'The quantity of GHG removal reversed.',
  }),
  externalDescription: msg({
    message: 'The quantity of GHG removal reversed.',
    context: 'GHG reversals',
  }),
};
const standardDistanceQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Distance',
    context: 'The distance traveled.',
  }),
  externalDescription: msg({
    message: 'The distance traveled.',
    context: 'Distance',
  }),
};

const currencyQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Currency quantity',
    context: 'The expenditure amount.',
  }),
  externalDescription: msg({
    message: 'The expenditure amount.',
    context: 'Currency quantity',
  }),
};

const currencyQuantityUsd: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Currency quantity in USD',
    context: 'The expenditure amount in USD.',
  }),
  externalDescription: msg({
    message: 'The expenditure amount in USD.',
    context: 'Currency quantity in USD',
  }),
};

const vehiclesSold: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Vehicle quantity',
    context: 'The number of vehicles sold.',
  }),
  externalDescription: msg({
    message: 'The number of vehicles sold.',
    context: 'Vehicle quantity',
  }),
};

const buildingKind: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Facility kind',
    context: 'The type of facility.',
  }),
  externalDescription: msg({
    message: 'The type of facility.',
    context: 'Facility kind',
  }),
};

const cloudVmInstanceType: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud virtual machine instance type',
    context: 'The virtual machine instance type for the cloud usage.',
  }),
  externalDescription: msg({
    message: 'The virtual machine instance type for the cloud usage.',
    context: 'Cloud virtual machine instance type',
  }),
};

const cloudRegion: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud region',
    context: `The identifier of the cloud provider's region. Examples: 'us-east-1' (AWS), 'asia-northeast2-a' (GCP).`,
  }),
  externalDescription: msg({
    message: `The identifier of the cloud provider's region. Examples: 'us-east-1' (AWS), 'asia-northeast2-a' (GCP).`,
    context: 'Cloud region',
  }),
};

const cloudService: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud service',
    context: `An identifier for the cloud service used. Examples: AWS Lambda, Google Storage.`,
  }),
  externalDescription: msg({
    message: `An identifier for the cloud service used. Examples: AWS Lambda, Google Storage.`,
    context: 'Cloud service',
  }),
};

const cloudServiceClass: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud service class',
    context: `An identifier for the cloud service class as compute, networking, storage or other.`,
  }),
  externalDescription: msg({
    message: `An identifier for the cloud service class as compute, networking, storage or other.`,
    context: 'Cloud service class',
  }),
};

const currency: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Currency',
    context: 'The currency of the expenditure.',
  }),
  externalDescription: msg({
    message: 'The currency of the expenditure.',
    context: 'Currency',
  }),
};

const locationCity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'City',
    context: 'The city in which the business activity took place.',
  }),
  externalDescription: msg({
    message: 'The city in which the business activity took place.',
    context: 'City',
  }),
};

const locationCountry: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Country',
    context: 'The country in which the business activity took place.',
  }),
  externalDescription: msg({
    message: 'The country in which the business activity took place.',
    context: 'Country',
  }),
};

const month: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Month',
    context: 'The month the business activity took place.',
  }),
  externalDescription: msg({
    message: 'The month the business activity took place.',
    context: 'Month',
  }),
};

const distanceUnit: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Distance unit',
    context: 'The unit of distance traveled.',
  }),
  externalDescription: msg({
    message: 'The unit of distance traveled.',
    context: 'Distance unit',
  }),
};

const waterRecyclingQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Water recycled',
    context: 'The quantity of water recycled.',
  }),
  externalDescription: msg({
    message: 'The quantity of water recycled.',
    context: 'Water recycled',
  }),
};

const waterStorageQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Water stored',
    context: 'The quantity of water stored.',
  }),
  externalDescription: msg({
    message: 'The quantity of water stored.',
    context: 'Water stored',
  }),
};

const waterDischargeQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Water discharged',
    context: 'The quantity of water discharged.',
  }),
  externalDescription: msg({
    message: 'The quantity of water discharged.',
    context: 'Water discharged',
  }),
};

const waterConsumptionQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Water consumed',
    context: 'The quantity of water consumed.',
  }),
  externalDescription: msg({
    message: 'The quantity of water consumed.',
    context: 'Water consumed',
  }),
};

const siteLandUseQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Site area',
    context: 'The area of the site.',
  }),
  externalDescription: msg({
    message: 'The area of the site.',
    context: 'Site area',
  }),
};

const materialWeight: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Material weight',
    context: 'The weight of the material',
  }),
  externalDescription: msg({
    message: 'The weight of the material',
    context: 'Material weight',
  }),
};

const productWeightQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Product weight',
    context: 'The weight of the product',
  }),
  externalDescription: msg({
    message: 'The weight of the product',
    context: 'Product weight',
  }),
};

const heatSource: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Heat source',
    context: 'The source of heat for the facility.',
  }),
  externalDescription: msg({
    message: 'The source of heat for the facility.',
    context: 'Heat source',
  }),
};

const ceeBuildingQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Building',
    context: 'The count of facilities represented.',
  }),
  externalDescription: msg({
    message: 'The count of facilities represented.',
    context: 'Building',
  }),
};

const ceeBuildingAreaQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Area',
    context: 'The area of the facility.',
  }),
  externalDescription: msg({
    message: 'The area of the facility.',
    context: 'Area',
  }),
};

const ceePercentRenewable: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Percent renewable',
    context:
      'The percentage of electricity consumption covered by clean power purchased (e.g., through EACs or a VPPA) that is not from the grid.',
  }),
  externalDescription: msg({
    message:
      'The percentage of electricity consumption covered by clean power purchased (e.g., through EACs or a VPPA) that is not from the grid.',
    context: 'Percent renewable',
  }),
};

const ceePercentOnsiteCleanPower: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Percent onsite clean power',
    context:
      'The percentage of electricity generated from renewable sources onsite.',
  }),
  externalDescription: msg({
    message:
      'The percentage of electricity generated from renewable sources onsite.',
    context: 'Percent onsite clean power',
  }),
};

const ceeEmployeeQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Employee quantity',
    context: 'The number of employees.',
  }),
  externalDescription: msg({
    message: 'The number of employees.',
    context: 'Employee quantity',
  }),
};

const ceeRemoteTimePercent: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Remote percentage',
    context: 'The percentage of time employees work remotely.',
  }),
  externalDescription: msg({
    message: 'The percentage of time employees work remotely.',
    context: 'Remote percentage',
  }),
};

const ceePrecalculatedEmissions: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Pre-calculated emissions',
    context: 'The pre-calculated kgCO2e.',
  }),
  externalDescription: msg({
    message: 'The pre-calculated emissions.',
    context: 'Pre-calculated kgCO2e.',
  }),
};

const ceeElectricityUsage: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Electricity usage',
    context: 'The electricity usage.',
  }),
  externalDescription: msg({
    message: 'The electricity usage.',
    context: 'Electricity usage',
  }),
};

const eeioModelKind: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'EEIO model kind',
    context: 'The kind of EEIO model.',
  }),
  externalDescription: msg({
    message: 'The kind of EEIO model.',
    context: 'EEIO model kind',
  }),
};

const cloudProvider: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Cloud provider',
    context: 'The cloud provider.',
  }),
  externalDescription: msg({
    message: 'The cloud provider.',
    context: 'Cloud provider',
  }),
};

const naicsCode: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Industry code',
    context: 'The industry code.',
  }),
  externalDescription: msg({
    message: 'The industry code.',
    context: 'Industry code',
  }),
};

const inflationAdjustmentNaicsCode: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Inflation adjustment industry code',
    context: 'The industry code used for inflation adjustment.',
  }),
  externalDescription: msg({
    message: 'The industry code used for inflation adjustment.',
    context: 'Inflation adjustment industry code',
  }),
};

/**
 * I18n message descriptors for BATs and their fields
 */
export const BAT_DISPLAY_CONFIG = {
  [GLOBAL_KEY]: {
    // eslint-disable-next-line @watershed/l10n-string-context, lingui/no-single-variables-to-translate
    displayName: msg({ message: `${GLOBAL_KEY}` }), // this should never be displayed to users itself!
    fields: {
      clean_power_percent: {
        displayName: msg({
          message: 'Clean power percent',
          context: 'The clean power percent of the facility.',
        }),
        externalDescription: msg({
          message: 'The clean power percent of the facility.',
          context: 'Clean power percent',
        }),
      },
      currency_quantity: currencyQuantity,
      distance_quantity: standardDistanceQuantity,
      distance_unit: distanceUnit,
      onsite_clean_power_percent: {
        displayName: msg({
          message: 'Onsite clean power percent',
          context:
            'The percentage of energy generated from renewable sources onsite',
        }),
        externalDescription: msg({
          message:
            'The percentage of energy generated from renewable sources onsite',
          context: 'Onsite clean power percent',
        }),
      },
      passenger_quantity: {
        displayName: msg({
          message: 'Passenger quantity',
          context: 'The number of passengers.',
        }),
        externalDescription: msg({
          message: 'The number of passengers.',
          context: 'Passenger quantity',
        }),
      },
      location_city: locationCity,
      location_country: locationCountry,
      month,
    },
  },

  employees: employeesMetadata,

  // Used for TSchema migration by Data Infra
  dummy_employees: {
    displayName: msg({
      message: 'Dummy employees',
      context: 'Activity data schema name',
    }),
    fields: {},
  },
  building_stationary_combustion: buildingStationaryCombustionMetadata,

  building_waste: buildingWasteMetadata,

  cloud_compute_usage: cloudComputeUsageMetadata,

  cloud_spend: cloudSpendMetadata,

  cloud_usage_and_spend: {
    displayName: msg({
      message: 'Cloud usage and spend',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Cloud usage or spend quantity',
          context:
            'The numeric value representing either usage (e.g., instance hours, bytes) or spend (e.g., dollars, credits) for cloud resources.',
        }),
        externalDescription: msg({
          message:
            'The numeric value representing either usage (e.g., instance hours, bytes) or spend (e.g., dollars, credits) for cloud resources.',
          context: 'Cloud usage or spend quantity',
        }),
      },
      currency_quantity: cloudSpendCurrencyQuantity,
      usage_quantity: cloudUsageQuantity,
      cloud_service: cloudService,
      cloud_provider_region: cloudRegion,
      cloud_vm_instance_type: cloudVmInstanceType,
      service_class: cloudServiceClass,
    },
  },

  cee_building: {
    displayName: msg({
      message: 'CEE Building',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: ceeBuildingQuantity, // building_quantity is the new functional unit
      building_quantity: ceeBuildingQuantity,
      building_area: ceeBuildingAreaQuantity,
      percent_renewable: ceePercentRenewable,
      building_kind: buildingKind,
      heat_source: heatSource,
    },
  },

  cee_building_scope_1: {
    displayName: msg({
      message: 'CEE Building Scope 1',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: ceeBuildingAreaQuantity,
    },
  },

  cee_building_scope_2: {
    displayName: msg({
      message: 'CEE Building Scope 2',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: ceeBuildingAreaQuantity,
    },
  },

  cee_building_scope_3: {
    displayName: msg({
      message: 'CEE Building Scope 3',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: ceeBuildingAreaQuantity,
    },
  },

  cee_cloud_spend: {
    displayName: msg({
      message: 'CEE Cloud Spend',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: currencyQuantityUsd,
      provider: cloudProvider,
      eeioModelKind,
    },
  },

  cee_commuting: {
    displayName: msg({
      message: 'CEE Commuting',
      context: 'Activity data schema name',
    }),
    fields: {
      employee_remote_time_percent: ceeRemoteTimePercent,
      quantity: ceeEmployeeQuantity,
    },
  },

  cee_eeio_scope_1: {
    displayName: msg({
      message: 'CEE EEIO Scope 1',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: currencyQuantityUsd,
    },
  },

  cee_electricity_usage: {
    displayName: msg({
      message: 'CEE Electricity Usage',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: ceeElectricityUsage,
      percent_renewable: ceePercentRenewable,
      percent_onsite_clean_power: ceePercentOnsiteCleanPower,
    },
  },

  cee_electricity_usage_scope_2: {
    displayName: msg({
      message: 'CEE Electricity Usage Scope 2',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  cee_electricity_usage_scope_3_3_td_loss: {
    displayName: msg({
      message: 'CEE Electricity Usage Scope 3.3 TD Loss',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  cee_electricity_usage_scope_3_3_wtt: {
    displayName: msg({
      message: 'CEE Electricity Usage Scope 3.3 WTT',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  cee_electricity_usage_scope_3_3_wtt_td_loss: {
    displayName: msg({
      message: 'CEE Electricity Usage Scope 3.3 WTT TD Loss',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  cee_flights: {
    displayName: msg({
      message: 'CEE Flights',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: standardDistanceQuantity,
    },
  },

  cee_home_office: {
    displayName: msg({
      message: 'CEE Home Office',
      context: 'Activity data schema name',
    }),
    fields: {
      employee_remote_time_percent: ceeRemoteTimePercent,
      quantity: ceeEmployeeQuantity,
    },
  },

  cee_naics_spend: {
    displayName: msg({
      message: 'CEE NAICS spend',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: currencyQuantityUsd,
      naics_code: naicsCode,
      inflation_adjustment_naics_code: inflationAdjustmentNaicsCode,
    },
  },

  cee_precalculated: {
    displayName: msg({
      message: 'CEE Precalculated',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: ceePrecalculatedEmissions,
    },
  },

  cee_vehicles: {
    displayName: msg({
      message: 'CEE Vehicles',
      context: 'Activity data schema name',
    }),
    fields: {
      vehicle_quantity: vehiclesSold,
      vehicle_make: {
        displayName: msg({
          message: 'Vehicle make',
          context: 'The make of the vehicle',
        }),
        externalDescription: msg({
          message: 'The make of the vehicle',
          context: 'Vehicle make description',
        }),
      },
      vehicle_model: {
        displayName: msg({
          message: 'Vehicle model',
          context: 'The model of the vehicle',
        }),
        externalDescription: msg({
          message: 'The model of the vehicle',
          context: 'Vehicle model description',
        }),
      },
      vehicle_year: {
        displayName: msg({
          message: 'Vehicle year',
          context: 'The year of the vehicle',
        }),
        externalDescription: msg({
          message: 'The year of the vehicle',
          context: 'Vehicle year description',
        }),
      },
      fuel_efficiency: {
        displayName: msg({
          message: 'Fuel efficiency',
          context: 'The fuel efficiency of the vehicle',
        }),
        externalDescription: msg({
          message: 'The fuel efficiency of the vehicle',
          context: 'Fuel efficiency description',
        }),
      },
      electricity_efficiency: {
        displayName: msg({
          message: 'Electricity efficiency',
          context: 'The electricity efficiency of the vehicle',
        }),
        externalDescription: msg({
          message: 'The electricity efficiency of the vehicle',
          context: 'Electricity efficiency description',
        }),
      },
      vehicle_category: {
        displayName: msg({
          message: 'Vehicle category',
          context: 'The category of the vehicle',
        }),
        externalDescription: msg({
          message: 'The category of the vehicle',
          context: 'Vehicle category description',
        }),
      },
      vehicle_powertrain_type: {
        displayName: msg({
          message: 'Vehicle powertrain type',
          context: 'The powertrain type of the vehicle',
        }),
        externalDescription: msg({
          message: 'The powertrain type of the vehicle',
          context: 'Vehicle powertrain type description',
        }),
      },
    },
  },

  consumer_goods_ecoinvent_material: {
    displayName: msg({
      message: 'Purchased materials: Ecoinvent',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: materialWeight,
    },
  },
  consumer_goods_material_assessment: {
    displayName: msg({
      message: 'Purchased materials: Watershed library',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: materialWeight,
    },
  },
  consumer_goods_packaging_material: {
    displayName: msg({
      message: 'Packaging',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: materialWeight,
    },
  },
  consumer_goods_textile_product_use_phase: {
    displayName: msg({
      message: 'Sold products: Textile use',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Quantity',
          context: 'The quantity of product orders',
        }),
        externalDescription: msg({
          message: 'The quantity of product orders',
          context: 'Quantity',
        }),
      },
      product_weight: {
        displayName: msg({
          message: 'Product weight',
          context: 'The weight of each order of product',
        }),
        externalDescription: msg({
          message: 'The weight of each order of product',
          context: 'Product weight',
        }),
      },
      product_weight_unit: {
        displayName: msg({
          message: 'Product weight unit',
          context: 'Name of a column in table of activity data',
        }),
        externalDescription: msg({
          message: 'The unit of the weight of each order of product',
          context: 'Description of a column in table of activity data',
        }),
      },
      dry_method: {
        displayName: msg({
          message: 'Dry method',
          context: 'Name of a column in table of activity data',
        }),
        externalDescription: msg({
          message: 'How the product is dried',
          context: 'Description of a column in table of activity data',
        }),
      },
      fabric_category: {
        displayName: msg({
          message: 'Fabric category',
          context: 'Name of a column in table of activity data',
        }),
        externalDescription: msg({
          message: 'The main type of fabric for the product.',
          context: 'Description of a column in table of activity data',
        }),
      },
      product_category: {
        displayName: msg({
          message: 'Product category',
          context: 'Name of a column in table of activity data',
        }),
        externalDescription: msg({
          message: 'The type of product',
          context: 'Description of a column in table of activity data',
        }),
      },
      wash_method: {
        displayName: msg({
          message: 'Wash method',
          context: 'Name of a column in table of activity data',
        }),
        externalDescription: msg({
          message: 'How the product is washed',
          context: 'Description of a column in table of activity data',
        }),
      },
    },
  },
  corporate_315_investments: corporate315InvestmentsMetadata,
  direct_ghg_emissions: directGhgEmissionsMetadata,

  data_transfer: dataTransferMetadata,

  electricity_consumption: electricityConsumptionMetadata,

  expenditure: expenditureMetadata,

  expenditure_mapped: expenditureMappedMetadata,
  finance_corporate_asset: financeCorporateAssetMetadata,
  non_building_electricity_consumption:
    nonBuildingElectricityConsumptionMetadata,

  electricity_consumption_datacenter: electricityConsumptionDatacenterMetadata,
  food_mass: {
    displayName: msg({
      message: 'Food mass',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Food mass',
          context: 'The mass of the food item.',
        }),
        externalDescription: msg({
          message: 'The mass of the food item.',
          context: 'Food mass',
        }),
      },
      food_processing_sector: {
        displayName: msg({
          message: 'Food processing sector',
          context: 'The processing sector for the purchased food item',
        }),
        externalDescription: msg({
          message: 'The processing sector for the purchased food item',
          context: 'Food processing sector',
        }),
      },
      food_item: {
        displayName: msg({
          message: 'Food item',
          context: 'The purchased food item',
        }),
        externalDescription: msg({
          message: 'The purchased food item',
          context: 'Food item',
        }),
      },
      food_use_case: {
        displayName: msg({
          message: 'Food use case',
          context: 'Specifies if the food is a purchased or a sold product',
        }),
        externalDescription: msg({
          message: 'Specifies if the food is a purchased or a sold product',
        }),
      },
    },
  },
  food_spend: {
    displayName: msg({
      message: 'Food expenses',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Food spend',
          context: 'Expenditure on the food item.',
        }),
        externalDescription: msg({
          message: 'Expenditure on the food item.',
          context: 'Food spend',
        }),
      },
    },
  },
  fuel_combustion: fuelCombustionMetadata,

  refrigerants: refrigerantsMetadata,

  sold_retail_good: soldRetailGoodMetadata,

  building_occupation: buildingOccupationMetadata,

  logistics: logisticsMetadata,

  lifetime_fuel_and_electricity_use_of_sold_products:
    lifetimeFuelAndElectricityUseOfSoldProductsMetadata,

  sold_refrigerant: soldRefrigerantMetadata,

  sold_vehicle: soldVehicleMetadata,

  lodging: lodgingMetadata,

  waste: wasteMetadata,

  waste_products: wasteProductsMetadata,

  water_usage: waterUsageMetadata,

  carbon_removal: carbonRemovalMetadata,

  ghg_removal: {
    displayName: msg({
      message: 'GHG removal',
      context: 'Activity data schema name',
    }),
    fields: {
      ghg_removal_quantity: ghgRemovalQuantity,
      ghg_reversals_quantity: ghgReversalsQuantity,
      quantity: ghgRemovalQuantity,
    },
  },

  carbon_credit_retirements: carbonCreditRetirementsMetadata,

  vehicles: vehiclesMetadata,
  tier_1_factories_activity_data: {
    displayName: msg({
      message: 'Supplier factories energy, waste, and water: Tier 1',
      context: 'Activity data schema name',
    }),
    fields: {
      biomass_non_wood_mj: {
        displayName: msg({
          message: 'Biomass (non-wood) quantity',
          context: 'Energy from biomass (non-wood).',
        }),
        externalDescription: msg({
          message: 'Energy from biomass (non-wood).',
          context: 'Biomass (non-wood) quantity',
        }),
      },
      biomass_wood_mj: {
        displayName: msg({
          message: 'Biomass (wood) quantity',
          context: 'Energy from biomass (wood).',
        }),
        externalDescription: msg({
          message: 'Energy from biomass (wood).',
          context: 'Biomass (wood) quantity',
        }),
      },
      biodiesel_fuel_mj: {
        displayName: msg({
          message: 'Biodiesel fuel',
          context: 'Energy from biodiesel.',
        }),
        externalDescription: msg({
          message: 'Energy from biodiesel.',
          context: 'Biodiesel fuel',
        }),
      },
      coal_mj: {
        displayName: msg({
          message: 'Coal quantity',
          context: 'Energy from coal.',
        }),
        externalDescription: msg({
          message: 'Energy from coal.',
          context: 'Coal quantity',
        }),
      },
      diesel_fuel_mj: {
        displayName: msg({
          message: 'Diesel fuel quantity',
          context: 'Energy from diesel fuel.',
        }),
        externalDescription: msg({
          message: 'Energy from diesel fuel.',
          context: 'Diesel fuel quantity',
        }),
      },
      district_heat_mj: {
        displayName: msg({
          message: 'District heat quantity',
          context: 'Energy from district heat.',
        }),
        externalDescription: msg({
          message: 'Energy from district heat.',
          context: 'District heat quantity',
        }),
      },
      electricity_mj: {
        displayName: msg({
          message: 'Electricity quantity',
          context: 'Energy from electricity.',
        }),
        externalDescription: msg({
          message: 'Energy from electricity.',
          context: 'Electricity quantity',
        }),
      },
      fuel_oil_mj: {
        displayName: msg({
          message: 'Fuel oil quantity',
          context: 'Energy from fuel oil.',
        }),
        externalDescription: msg({
          message: 'Energy from fuel oil.',
          context: 'Fuel oil quantity',
        }),
      },
      gasoline_mj: {
        displayName: msg({
          message: 'Gasoline quantity',
          context: 'Energy from gasoline.',
        }),
        externalDescription: msg({
          message: 'Energy from gasoline.',
          context: 'Gasoline quantity',
        }),
      },
      liquified_natural_gas_mj: {
        displayName: msg({
          message: 'LNG quantity',
          context: 'Energy from liquified natural gas.',
        }),
        externalDescription: msg({
          message: 'Energy from liquified natural gas.',
          context: 'LNG quantity',
        }),
      },
      liquified_petroleum_gas_mj: {
        displayName: msg({
          message: 'Liquified petroleum gas quantity',
          context: 'Energy from liquified petroleum gas.',
        }),
        externalDescription: msg({
          message: 'Energy from liquified petroleum gas.',
          context: 'LNG quantity',
        }),
      },
      natural_gas_mj: {
        displayName: msg({
          message: 'Natural gas quantity',
          context: 'Energy from natural gas.',
        }),
        externalDescription: msg({
          message: 'Energy from natural gas.',
          context: 'Natural gas quantity',
        }),
      },
      propane_mj: {
        displayName: msg({
          message: 'Propane quantity',
          context: 'Energy from propane.',
        }),
        externalDescription: msg({
          message: 'Energy from propane.',
          context: 'Propane quantity',
        }),
      },
      steam_mj: {
        displayName: msg({
          message: 'Steam quantity',
          context: 'Energy from steam.',
        }),
        externalDescription: msg({
          message: 'Energy from steam.',
          context: 'Steam quantity',
        }),
      },
      quantity: {
        displayName: msg({
          message: 'Factory count',
          context: 'Tier 1 factory for the reporting company',
        }),
        externalDescription: msg({
          message: 'Tier 1 factory for the reporting company',
          context: 'Factory count',
        }),
      },
      waste_kg: {
        displayName: msg({
          message: 'Waste quantity',
          context: 'Mass of waste.',
        }),
        externalDescription: msg({
          message: 'Mass of waste.',
          context: 'Waste quantity',
        }),
      },
      water_liters: {
        displayName: msg({
          message: 'Water quantity',
          context: 'Volume of water.',
        }),
        externalDescription: msg({
          message: 'Volume of water.',
          context: 'Water quantity',
        }),
      },
      onsite_clean_power_percent: {
        displayName: msg({
          message: 'Onsite clean power percent',
          context:
            'The percentage of energy generated from renewable sources onsite',
        }),
        externalDescription: msg({
          message:
            'The percentage of energy generated from renewable sources onsite',
          context: 'Onsite clean power percent',
        }),
      },
    },
  },
  tier_1_factories_spend: {
    displayName: msg({
      message: 'Supplier factories expenses: Tier 1',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: currencyQuantity,
    },
  },

  travel_air: travelAirMetadata,

  travel_private_jets: travelPrivateJetsMetadata,

  travel_rail: travelRailMetadata,

  historical_emissions: {
    displayName: msg({
      message: 'Historical emissions',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  historical_emissions_pv2: {
    displayName: msg({
      message: 'Historical emissions from pipeline v2',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  water_consumption: {
    displayName: msg({
      message: 'Water consumption',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: waterConsumptionQuantity,
    },
  },

  water_recycling: {
    displayName: msg({
      message: 'Water recycling',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: waterRecyclingQuantity,
    },
  },

  water_storage: {
    displayName: msg({
      message: 'Water storage',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: waterStorageQuantity,
    },
  },

  water_discharge: {
    displayName: msg({
      message: 'Water discharge',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: waterDischargeQuantity,
    },
  },

  expenditure_unmapped: expenditureUnmappedMetadata,

  physical_goods_purchase: {
    displayName: msg({
      message: 'Physical goods purchase',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  purchase_orders: {
    displayName: msg({
      message: 'Purchase orders',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  crypto_holdings: {
    displayName: msg({
      message: 'Crypto holdings',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  crypto_transactions: {
    displayName: msg({
      message: 'Crypto transactions',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  consumer_goods_product_assessment: {
    displayName: msg({
      message: 'Product assessment',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  consumer_goods_textile_product_manufacturing: {
    displayName: msg({
      message: 'Textile product manufacturing',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  corporate_asset_holding: {
    displayName: msg({
      message: 'Corporate asset holdings',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly outstanding amount held',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly outstanding amounts.',
          context: 'Total monthly outstanding amount held',
        }),
      },
    },
  },

  real_estate_asset_holding: {
    displayName: msg({
      message: 'Real Estate asset holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly outstanding amount held',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly outstanding amounts.',
          context: 'Total monthly outstanding amount held',
        }),
      },
    },
  },

  asset_group_holding: {
    displayName: msg({
      message: 'Asset Group holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly outstanding amount held',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly outstanding amounts.',
          context: 'Total monthly outstanding amount held',
        }),
      },
    },
  },

  personal_motor_vehicle_insurance_holding: {
    displayName: msg({
      message: 'Personal motor vehicle insurance holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Number of holdings, always 1.',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'Each holding represents 1 personal motor vehicle insurance policy.',
          context: 'Holding for a personal motor vehicle insurance policy.',
        }),
      },
    },
  },

  sovereign_debt_holding: {
    displayName: msg({
      message: 'Sovereign debt holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly exposure amount',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly exposure amounts.',
          context:
            'Description of a field of data describing amounts of money.',
        }),
      },
    },
  },

  insurance_commercial_line_holding: {
    displayName: msg({
      message: 'Insurance commercial line holding',
      context:
        'Activity data schema name; represents a commercial line financial holding in a corporate entity.',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Number of holdings',
          context: 'The number of holdings this field represents.',
        }),
        externalDescription: msg({
          message:
            'Each holding represents the premium for a commercial lines holding.',
          context:
            'Description of a numeric field of data that shows the premium for a commercial line financial holding.',
        }),
      },
    },
  },

  legacy_finance_emissions: {
    displayName: msg({
      message: 'Legacy finance emissions',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  substances: {
    displayName: msg({
      message: 'Substances',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  substance_outflow: {
    displayName: msg({
      message: 'Substance outflow',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Substance quantity',
          context: 'The quantity of the substance released.',
        }),
        externalDescription: msg({
          message: 'The quantity of the substance released.',
          context: 'Substance quantity',
        }),
      },
      unit: {
        displayName: msg({
          message: 'Substance unit',
          context: 'The unit of the substance released.',
        }),
        externalDescription: msg({
          message: 'The unit of the substance released.',
          context: 'Substance unit',
        }),
      },
      cas_number: {
        displayName: msg({
          message: 'CAS number',
          context: 'Chemical Abstracts Service registry number.',
        }),
        externalDescription: msg({
          message: 'Chemical Abstracts Service registry number.',
        }),
      },
      eu_prtr_group: {
        displayName: msg({
          message: 'E-PRTR group',
          context:
            'European Pollutant Release and Transfer Register group of the substance.',
        }),
        externalDescription: msg({
          message:
            'European Pollutant Release and Transfer Register group of the substance.',
        }),
      },
      substance_outflow_medium: {
        displayName: msg({
          message: 'Substance outflow medium',
          context: 'The medium through which the substance is released.',
        }),
        externalDescription: msg({
          message: 'The medium through which the substance is released.',
          context: 'Substance outflow medium',
        }),
      },
    },
  },

  substance_inflow: {
    displayName: msg({
      message: 'Substance inflow',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Substance quantity',
          context: 'The quantity of the substance that enters the boundary.',
        }),
        externalDescription: msg({
          message: 'The quantity of the substance released.',
          context: 'Substance quantity',
        }),
      },
      unit: {
        displayName: msg({
          message: 'Substance unit',
          context: 'The unit of the substance that enters the boundary.',
        }),
        externalDescription: msg({
          message: 'The unit of the substance that enters the boundary.',
          context: 'Substance unit',
        }),
      },
      cas_number: {
        displayName: msg({
          message: 'CAS number',
          context: 'Chemical Abstracts Service registry number.',
        }),
        externalDescription: msg({
          message: 'Chemical Abstracts Service registry number.',
        }),
      },
      substance_inflow_medium: {
        displayName: msg({
          message: 'Substance inflow medium',
          context:
            'The medium through which the substance enters the boundary.',
        }),
        externalDescription: msg({
          message:
            'The medium through which the substance enters the boundary.',
          context: 'Substance inflow medium',
        }),
      },
    },
  },

  microplastics_outflow: {
    displayName: msg({
      message: 'Microplastics outflow',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Microplastics quantity',
          context: 'The quantity of microplastics released.',
        }),
        externalDescription: msg({
          message: 'The quantity of microplastics released.',
          context: 'Microplastics quantity',
        }),
      },
      microplastics_outflow_medium: {
        displayName: msg({
          message: 'Microplastics outflow medium',
          context: 'The medium through which the microplastics are released.',
        }),
        externalDescription: msg({
          message: 'The medium through which the microplastics are released.',
          context: 'Microplastics outflow medium',
        }),
      },
    },
  },

  site_land_use: {
    displayName: msg({
      message: 'Site land use',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: siteLandUseQuantity,
      site_identifier: {
        displayName: msg({
          message: 'Site identifier',
          context: 'Unique identifier for the site.',
        }),
        externalDescription: msg({
          message: 'Unique identifier for the site.',
          context: 'Site identifier',
        }),
      },
    },
  },

  intensity_denominator_monetary: {
    displayName: msg({
      message: 'Income',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  intensity_denominator_custom: {
    displayName: msg({
      message: 'Custom intensity denominator',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  custom: {
    displayName: msg({
      message: 'Custom',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  legacy_clean_power: {
    displayName: msg({
      message: 'Clean power',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  cadt: {
    displayName: msg({
      message: 'Clean activity data table',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  em_development_bat: {
    displayName: msg({
      message: 'EM development',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Quantity',
          context: 'The quantity of the emissive activity.',
        }),
        externalDescription: msg({
          message: 'The quantity of the emissive activity.',
          context: 'Quantity',
        }),
      },
    },
  },

  activity_with_custom_ef: {
    displayName: msg({
      message: 'Custom activity with custom emissions factors',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Quantity',
          context: 'The quantity of the emissive activity.',
        }),
        externalDescription: msg({
          message: 'The quantity of the emissive activity.',
          context: 'Quantity',
        }),
      },
    },
  },

  sold_product_circularity: {
    displayName: msg({
      message: 'Sold products: Durability',
      context: 'Activity data schema name',
    }),
    fields: {
      product_id: {
        displayName: msg({
          message: 'Product ID',
          context:
            'Unique way to identify the product (e.g. name, SKU, product key).',
        }),
        externalDescription: msg({
          message:
            'Unique way to identify the product (e.g. name, SKU, product key).',
          context: 'Product ID',
        }),
      },
    },
  },

  sold_product_recyclability: {
    displayName: msg({
      message: 'Sold products: Recyclability',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: productWeightQuantity,
      product_id: {
        displayName: msg({
          message: 'Product ID',
          context:
            'Unique way to identify the product (e.g. name, SKU, product key).',
        }),
        externalDescription: msg({
          message:
            'Unique way to identify the product (e.g. name, SKU, product key).',
          context: 'Product ID',
        }),
      },
    },
  },

  workplace_reports: {
    displayName: msg({
      message: 'Workplace incidents',
      context: 'Activity data schema name',
    }),
    fields: {
      incident_id: {
        displayName: msg({
          message: 'Worklace incident ID',
          context: 'Unique way to identify the incident.',
        }),
        externalDescription: msg({
          message: 'Unique way to identify the incident.',
          context: 'Worklace incident ID',
        }),
      },
      currency,
      monetary_remediation_quantity: currencyQuantity,
    },
  },

  health_and_safety_incidents: {
    displayName: msg({
      message: 'Health and safety incidents',
      context: 'Activity data schema name',
    }),
    fields: {
      incident_id: {
        displayName: msg({
          message: 'Health and safety incident ID',
          context: 'Unique way to identify the incident.',
        }),
        externalDescription: msg({
          message: 'Unique way to identify the incident.',
          context: 'Health and safety incident ID',
        }),
      },
    },
  },

  severe_human_rights_incidents: {
    displayName: msg({
      message: 'Severe human-rights incidents',
      context: 'Activity data schema name',
    }),
    fields: {
      incident_id: {
        displayName: msg({
          message: 'Severe human rights incident ID',
          context: 'Unique way to identify the incident.',
        }),
        externalDescription: msg({
          message: 'Unique way to identify the incident.',
          context: 'Severe human rights incident ID',
        }),
      },
    },
  },

  anti_bribery_and_corruption_incidents: {
    displayName: msg({
      message: 'Bribery and corruption',
      context: 'Activity data schema name',
    }),
    fields: {
      incident_id: {
        displayName: msg({
          message: 'Anti-bribery and corruption incident ID',
          context: 'Unique way to identify the incident.',
        }),
        externalDescription: msg({
          message: 'Unique way to identify the incident.',
          context: 'Anti-bribery and corruption incident ID',
        }),
      },
    },
  },

  payment_orders: {
    displayName: msg({
      message: 'Payment orders',
      context: 'Activity data schema name',
    }),
    fields: {
      purchase_order_id: {
        displayName: msg({
          message: 'Payment order ID',
          context: 'Unique way to identify the payment order.',
        }),
        externalDescription: msg({
          message: 'Unique way to identify the payment order.',
          context: 'Payment order ID',
        }),
      },
    },
  },

  lobbying: {
    displayName: msg({
      message: 'Lobbying',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: currencyQuantity,
      currency,
    },
  },

  workers_social: {
    displayName: msg({
      message: 'Workforce social data',
      context: 'Activity data schema name',
    }),
    fields: {
      num_paid_below_adequate_wage: {
        displayName: msg({
          message: 'Number paid below adequate wage',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'Number of workers paid below the adequate wage benchmark.',
          context: 'description of data field',
        }),
      },
      num_covered_under_collective_bargaining: {
        displayName: msg({
          message: 'Workers covered under collective bargaining count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'Number of workers covered by collective bargaining agreements.',
          context: 'description of data field',
        }),
      },
      num_covered_by_workers_representation: {
        displayName: msg({
          message: 'Workers covered by workers representation count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'Number of workers covered by workers representation.',
          context: 'description of data field',
        }),
      },
      num_covered_by_health_and_safety_management_system: {
        displayName: msg({
          message:
            'Workers covered by health and safety management system count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'Number of workers covered by the health and safety management systems based on legal requirements and/or recognised standards or guidelines, as applicable to your company. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.',
          context: 'description of data field',
        }),
      },
      num_participated_in_reviews: {
        displayName: msg({
          message: 'Workers participated in performance reviews count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'Number of workers that participated in regular performance and career development reviews. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.',
          context: 'description of data field',
        }),
      },
      average_number_training_hours: {
        displayName: msg({
          message: 'Average number of training hours',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'Average number of training hours the workers received in the period.',
          context: 'description of data field',
        }),
      },
      num_workers: {
        displayName: msg({
          message: 'Workers count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'The quantity of workers.',
          context: 'description of data field',
        }),
      },
      quantity_hours_worked: {
        displayName: msg({
          message: 'Quantity of hours worked',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'The quantity of hours worked by the employees of the operation during the period.',
          context: 'description of data field',
        }),
      },
      is_top_level_management: {
        displayName: msg({
          message: 'Is top-level management',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'If the employee is top-level management or not.',
          context: 'description of data field',
        }),
      },
      age: {
        displayName: msg({
          message: 'Age group',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'The age of employee group.',
          context: 'description of data field',
        }),
      },
      num_with_disabilities: {
        displayName: msg({
          message: 'Workers with disabilities count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'The number of employees with disabilities. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.',
          context: 'description of data field',
        }),
      },
      num_entitled_to_take_family_related_leave: {
        displayName: msg({
          message: 'Workers entitled to take family related leave count',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            "Number of employees entitled to take family-related leave, which may include maternity leave, paternity leave, parental leave, carers' leave, etc, as applicable in your company or region of operation. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.",
          context: 'description of data field',
        }),
      },
      num_taken_family_related_leave: {
        displayName: msg({
          message: 'Workers who took family related leave quantity',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            "Number of employees that took family-related leave, which may include maternity leave, paternity leave, parental leave, carers' leave, etc, as applicable in your company or region of operation, during the period.",
          context: 'description of data field',
        }),
      },
      quantity_terminated_contract: {
        displayName: msg({
          message: 'Workers with terminated contract quantity',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message:
            'The number of employees which terminated the contract during the period.',
          context: 'description of data field',
        }),
      },
      employee_category: {
        displayName: msg({
          message: 'Employee category',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'The category of employee.',
          context: 'description of data field',
        }),
      },
      non_employee_type: {
        displayName: msg({
          message: 'Non-employee type',
          context: 'Activity data schema name',
        }),
        externalDescription: msg({
          message: 'The type of non-employee.',
          context: 'description of data field',
        }),
      },
    },
  },
} as const satisfies Record<string, BatMetadataConfigEntry>;

// -----> Below is the interface implementation. We should not need to change
// this just because we're updating definition.

// Display names are required but descriptions are optional so that we can add
// descriptions over time without breaking changes.

type FieldMetadataDef = {
  displayName: string;
  externalDescription?: string;
};

type BatMetadataDef = {
  /**
   * All fields, keyed by field name.
   */
  fields: Map<string, FieldMetadataMessageDescriptorDef>;

  /**
   * A human-readable display name for this BAT.
   */
  displayName: MessageDescriptor;
};

export type MetadataDefinedPrimaryTypes = Exclude<
  keyof typeof BAT_DISPLAY_CONFIG,
  typeof GLOBAL_KEY
>;
// Assert that all BATs in the current schema are covered by the metadata
// We don't assert that they are identical, since we want metadata coverage
// to live on even after a BAT is removed from †he current version.
type UncoveredCurrentBATs = Exclude<
  Exclude<BusinessActivityTypeName, MetadataDefinedPrimaryTypes>,
  // Why is this here? We have a list of test only types in the types file,
  // that were added to write integration tests. We should clean these up
  // but for now we will just ensure we can skip them in this customer facing metadata
  BatTypeNamesForTestingOnly
>;
assertNoTypeDifferences<UncoveredCurrentBATs, never>(noTypeDifferences);

// Keyed by BAT name.
const batMetadataLookup = new Map<string, BatMetadataDef>(
  Object.entries(BAT_DISPLAY_CONFIG).map(([batName, batDef]) => {
    return [
      batName,
      {
        fields: new Map(Object.entries(batDef.fields)),
        displayName: batDef.displayName,
      },
    ];
  })
);

export type BatDisplayConfig = typeof BAT_DISPLAY_CONFIG;
export type BatSchemaName = keyof BatDisplayConfig;

/**
 * BatMetadata exposes functions for accessing BAT metadata definitions.
 */
export class BatMetadata {
  static getFieldMetadataMessageDescriptors<
    B extends BatSchemaName,
    F extends keyof BatDisplayConfig[B]['fields'],
  >(batName: B, fieldName: F): FieldMetadataMessageDescriptorDef {
    const batSchemaMetadata = BAT_DISPLAY_CONFIG[batName];
    return batSchemaMetadata?.fields[
      fieldName as keyof BatDisplayConfig[BatSchemaName]['fields']
    ];
  }

  /**
   * Returns the field metadata for a particular BAT field, or undefined if
   * there is no metadata defined for that field.
   */
  static getFieldMetadata(
    batName: string,
    fieldName: string,
    scopedI18n: I18n = i18n
  ): FieldMetadataDef | undefined {
    const fieldMetadata: FieldMetadataMessageDescriptorDef | undefined =
      BatMetadata.getFieldMetadataMessageDescriptors(
        batName as BatSchemaName,
        fieldName as keyof BatDisplayConfig[BatSchemaName]['fields']
      );
    if (!fieldMetadata) {
      return;
    }
    return {
      displayName: scopedI18n._(fieldMetadata.displayName),
      externalDescription: fieldMetadata.externalDescription
        ? scopedI18n._(fieldMetadata.externalDescription)
        : undefined,
    };
  }

  /**
   * Our BAT names are slugs like `building_stationary_combustion`. This
   * returns a human-readable display name, like "Building stationary combustion".
   * @deprecated Use BusinessActivityTypeService.getBatDisplayName instead. We now
   * have custom activity types that can't have static names.
   */
  static getDisplayName(batName: string, scopedI18n: I18n = i18n): string {
    BadDataError.invariant(
      batName !== GLOBAL_KEY,
      'Cannot get display name for global BAT name key'
    );
    const mappedName = batMetadataLookup.get(batName)?.displayName;
    if (!mappedName) {
      captureException(
        new Error(
          `[soft invariant] Could not fetch display name for BAT ${batName}. ` +
            'Please confirm the bat name is covered in BatMetadata.ts. Note that every BAT should ' +
            'remain covered in the metadata even if the BAT is removed from the current version.'
        ),
        { level: 'warning' }
      );
    }
    return mappedName ? scopedI18n._(mappedName) : batName;
  }

  /**
   * Returns the display name for a BAT as a MessageDescriptor, or undefined
   * if not found.
   * @deprecated Use BusinessActivityTypeService.getBatDisplayName instead. We now
   * have custom activity types that can't have static names.
   */
  static getDisplayNameMessageDescriptor(
    batName: string
  ): MessageDescriptor | undefined {
    return batMetadataLookup.get(batName)?.displayName ?? undefined;
  }

  /**
   * Applies display name overrides for activity types that need disambiguation.
   * Currently only applies to expenditure types to distinguish legacy vs standard.
   * Returns the full display name to use.
   *
   * This is a temporary override we're applying only in flexible footprint until
   * we're able to make the switch more broadly in the UI and won't need the override.
   */
  static applyDisplayNameOverride(
    displayName: string,
    batName: string,
    i18n: I18n
  ): string {
    if (batName === 'expenditure') {
      return i18n._(
        msg({
          message: 'Expenses (legacy)',
          context: 'Display name for legacy expenditure activity type',
        })
      );
    }
    return displayName;
  }
}
